import type { ComponentConfig } from "@puckeditor/core";
import dynamic from "next/dynamic";
import type { ImportStructureBlockProps } from "@/puck/types/blockProps";
import type { StudioBlockCatalogKind } from "@/utils/studioBlockCatalog";
import { getImportStructureLabel } from "@/utils/studioPuckJson/internalComponents";

// Loaded lazily (client-only) so importing the Puck config from server code or
// tests does not pull in the Puck editor runtime (@puckeditor/core + dnd-kit).
const ImportStructureField = dynamic(
  () =>
    import("@/app/studio/pages/[slug]/ImportStructureField").then(
      (module) => module.ImportStructureField,
    ),
  { ssr: false },
);

function ImportStructurePlaceholder() {
  return (
    <section className="not-prose my-4 flex flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-gail-primary/50 bg-gail-primary/5 px-6 py-10 text-center">
      <span className="rounded-full bg-gail-primary/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.22em] text-gail-primary">
        Import structure
      </span>
      <p className="max-w-md font-monument text-sm text-black/70">
        Copy a block or page JSON, then use <strong>Import JSON</strong> (in the
        sidebar or this block&apos;s toolbar) to replace this placeholder.
        Saving stays disabled until every import structure placeholder is
        resolved.
      </p>
    </section>
  );
}

/**
 * A placeholder block that expands into imported JSON in place. It is
 * intentionally excluded from AI imports, the block reference, and the public
 * catalog — it exists only as an authoring aid inside Studio.
 */
export function createImportStructureConfig(
  kind: StudioBlockCatalogKind,
): ComponentConfig<ImportStructureBlockProps> {
  return {
    label: getImportStructureLabel(),
    fields: {
      jsonImport: {
        type: "custom",
        label: "Import JSON",
        render: () => <ImportStructureField kind={kind} />,
      },
    },
    defaultProps: {
      jsonImport: "",
    },
    render: () => <ImportStructurePlaceholder />,
  };
}
